import { useState } from 'react'
import Select from 'react-select'
import { DateField, TimeField } from '@astro/date-field'
import { ksSelectStyles } from './StudioBits'
import LocationSelect from './LocationSelect'

const GENDER_OPTIONS = ['Male', 'Female', 'Other'].map((g) => ({ value: g, label: g }))

const BLANK = { name: '', gender: 'Male', dob: '', tob: '', pob: '', location: null }

/**
 * The single birth-details form that powers every studio part. Submits raw
 * field values up — the place-of-birth autocomplete resolves the exact
 * coordinates on pick, which are shared with all parts.
 *
 * Passing `initial` (a saved kundli, via `savedToBirth`) turns it into the
 * saved-kundli editor: prefilled, a compact two-column grid, its own field
 * ids (the studio's form may be mounted too) and a "Save Changes" button.
 * `children` render below the birth fields — the editor's label & notes.
 */
export default function BirthDetailsForm({ onGenerate, busy, initial = null, children = null }) {
  const editing = Boolean(initial)
  const id = (key) => (editing ? `ks-edit-${key}` : `ks-${key}`)
  const [form, setForm] = useState(() => (initial ? { ...BLANK, ...initial } : BLANK))
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }))

  const submit = (e) => {
    e.preventDefault()
    onGenerate(form)
  }

  return (
    <form className={`ks-form${editing ? ' ks-form--edit' : ''}`} onSubmit={submit}>
      <div className="ks-form-grid">
        <div className="ks-field-name">
          <label className="flbl" htmlFor={id('name')}>Full Name</label>
          <input id={id('name')} value={form.name} onChange={set('name')} className="field" placeholder="Your name" />
        </div>
        <div>
          <label className="flbl" htmlFor={id('gender')}>Gender</label>
          <Select
            inputId={id('gender')}
            options={GENDER_OPTIONS}
            value={GENDER_OPTIONS.find((o) => o.value === form.gender) ?? null}
            onChange={(o) => setForm((f) => ({ ...f, gender: o?.value || 'Male' }))}
            styles={ksSelectStyles}
            isSearchable={false}
            placeholder="Select gender"
          />
        </div>
        <div>
          <label className="flbl" htmlFor={id('dob')}>Date of Birth *</label>
          <DateField id={id('dob')} value={form.dob} onChange={set('dob')} required />
        </div>
        <div>
          <label className="flbl" htmlFor={id('tob')}>Time of Birth *</label>
          <TimeField id={id('tob')} value={form.tob} onChange={set('tob')} required />
        </div>
        <div className="ks-field-pob">
          <label className="flbl" htmlFor={id('pob')}>Place of Birth{editing ? ' *' : ''}</label>
          <LocationSelect
            inputId={id('pob')}
            value={form.location}
            onChange={(loc) => setForm((f) => ({ ...f, location: loc, pob: loc?.Name || '' }))}
            placeholder="Search your birth city…"
          />
        </div>
      </div>
      {children}
      <div className="ks-form-foot">
        <p className="ks-form-note">
          Exact birth time gives exact charts — even a few minutes shift the Lagna. Timings assume IST for
          unrecognised places.
        </p>
        <button type="submit" className="ks-submit shine-btn ks-generate" disabled={busy}>
          {editing ? (busy ? 'Saving…' : 'Save Changes') : busy ? 'Casting charts…' : 'Generate Kundli'}
        </button>
      </div>
    </form>
  )
}
